import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlayerStore, useSessionError } from '../store/playerStore';
import { episodeWatchPath } from '../lib/utils';
import { QueuePanel } from './QueuePanel';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import PaywallModal from './PaywallModal';
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  X,
  Minimize2,
  Maximize2,
  ChevronUp,
  ChevronDown,
  Music,
  ListMusic,
  SkipForward,
  Expand,
  GripVertical,
  AlertTriangle,
} from 'lucide-react';

/**
 * FloatingPlayer - Pure UI Component
 * 
 * This component contains NO media elements. It's just a "remote control"
 * that reads and writes to the playerStore. The actual <audio>/<video>
 * elements live in MediaEngine.jsx.
 */
export function FloatingPlayer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useAuth();
  
  // Store state
  const currentTrack = usePlayerStore(state => state.currentTrack);
  const queue = usePlayerStore(state => state.queue);
  const isPlaying = usePlayerStore(state => state.isPlaying);
  const currentTime = usePlayerStore(state => state.currentTime);
  const duration = usePlayerStore(state => state.duration);
  const volume = usePlayerStore(state => state.volume);
  const isMuted = usePlayerStore(state => state.isMuted);
  const isMinimized = usePlayerStore(state => state.isMinimized);
  const isVideoExpanded = usePlayerStore(state => state.isVideoExpanded);
  const showQueue = usePlayerStore(state => state.showQueue);
  const miniPlayerPosition = usePlayerStore(state => state.miniPlayerPosition);
  const miniPlayerSize = usePlayerStore(state => state.miniPlayerSize);
  const sessionError = useSessionError();
  const mediaError = usePlayerStore(state => state.mediaError);
  
  // Store actions
  const togglePlay = usePlayerStore(state => state.togglePlay);
  const seek = usePlayerStore(state => state.seek);
  const seekRelative = usePlayerStore(state => state.seekRelative);
  const changeVolume = usePlayerStore(state => state.changeVolume);
  const toggleMute = usePlayerStore(state => state.toggleMute);
  const clearSessionError = usePlayerStore(state => state.clearSessionError);
  const clearMediaError = usePlayerStore(state => state.clearMediaError);
  const closePlayer = usePlayerStore(state => state.closePlayer);
  const skipNext = usePlayerStore(state => state.skipNext);
  const setIsMinimized = usePlayerStore(state => state.setIsMinimized);
  const setIsVideoExpanded = usePlayerStore(state => state.setIsVideoExpanded);
  const setShowQueue = usePlayerStore(state => state.setShowQueue);
  const setMiniPlayerPosition = usePlayerStore(state => state.setMiniPlayerPosition);
  const setMiniPlayerSize = usePlayerStore(state => state.setMiniPlayerSize);
  const trialExpiredPaywall = usePlayerStore(state => state.trialExpiredPaywall);
  const setTrialExpiredPaywall = usePlayerStore(state => state.setTrialExpiredPaywall);

  // Drag & Resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [wasDragged, setWasDragged] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragStartPos = useRef({ x: 0, y: 0 });
  const miniPlayerRef = useRef(null);
  
  const isVideo = currentTrack?.media_type === 'video';
  const hasTrack = !!currentTrack;
  
  // Check if we're on the episode page or video player page for current track
  const isOnEpisodePage = location.pathname === `/episode/${currentTrack?.id}`;
  const isOnVideoPage = location.pathname === `/watch/${currentTrack?.id}`;
  const isOnAnyVideoPage = location.pathname.startsWith('/watch/'); // Hide player on any video page
  const isOnCurrentTrackPage = isOnEpisodePage || isOnVideoPage;
  
  // UI states - hide everything when on any video page
  // Audio always shows the same unified mini bar (no separate minimized/expanded states)
  const showAudioPlayer = hasTrack && !isVideo && !isOnCurrentTrackPage && !isOnAnyVideoPage;
  const showVideoMiniPlayer = hasTrack && isVideo && !isOnCurrentTrackPage && !isVideoExpanded && !isOnAnyVideoPage;
  const showVideoExpandedOverlay = hasTrack && isVideo && !isOnCurrentTrackPage && isVideoExpanded && !isOnAnyVideoPage;
  
  // Progress percentage
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle progress bar click
  const handleProgressClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    seek(percent * duration);
  }, [duration, seek]);
  
  // Collapse back to mini player
  const handleCollapse = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsVideoExpanded(false);
  }, [setIsVideoExpanded]);
  
  // Native fullscreen (explicit button only)
  const handleNativeFullscreen = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Request fullscreen on the video canvas element if available
    const videoCanvas = document.querySelector('[data-testid="video-canvas"]');
    if (videoCanvas) {
      if (videoCanvas.requestFullscreen) {
        videoCanvas.requestFullscreen();
      } else if (videoCanvas.webkitRequestFullscreen) {
        videoCanvas.webkitRequestFullscreen();
      }
    }
  }, []);
  
  // ========== DRAG HANDLERS ==========
  const handleDragStart = useCallback((e) => {
    if (isResizing) return;
    // Don't start drag on buttons
    if (e.target.closest('button')) return;
    
    e.preventDefault();
    setIsDragging(true);
    setWasDragged(false);
    
    // Store start position to detect actual drag vs click
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    
    const rect = miniPlayerRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  }, [isResizing]);
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    
    // Check if actually dragged (moved more than 5px)
    const dx = Math.abs(e.clientX - dragStartPos.current.x);
    const dy = Math.abs(e.clientY - dragStartPos.current.y);
    if (dx > 5 || dy > 5) {
      setWasDragged(true);
    }
    
    // Constrain to viewport
    const maxX = window.innerWidth - miniPlayerSize.width;
    const maxY = window.innerHeight - miniPlayerSize.height;
    
    setMiniPlayerPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, miniPlayerSize, setMiniPlayerPosition]);
  
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    // Reset wasDragged after a short delay to allow click handler to check it
    setTimeout(() => setWasDragged(false), 100);
  }, []);
  
  // ========== RESIZE HANDLERS ==========
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);
  
  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !miniPlayerRef.current) return;
    
    const rect = miniPlayerRef.current.getBoundingClientRect();
    
    // Calculate new size based on mouse position (resizing from top-left)
    const newWidth = rect.right - e.clientX;
    const newHeight = newWidth * (9/16); // Maintain 16:9 ratio
    
    // Constrain size
    const minWidth = 200;
    const maxWidth = 600;
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    const constrainedHeight = constrainedWidth * (9/16);
    
    setMiniPlayerSize({
      width: constrainedWidth,
      height: constrainedHeight,
    });
    
    // Adjust position to keep right edge fixed
    if (miniPlayerPosition.x !== null) {
      const newX = rect.right - constrainedWidth;
      setMiniPlayerPosition({
        x: Math.max(0, newX),
        y: miniPlayerPosition.y,
      });
    }
  }, [isResizing, miniPlayerPosition, setMiniPlayerPosition, setMiniPlayerSize]);
  
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);
  
  // Global mouse event handlers for drag/resize
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!currentTrack) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekRelative(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekRelative(10);
          break;
        case 'KeyM':
          e.preventDefault();
          toggleMute();
          break;
        case 'Escape':
          if (isVideoExpanded) {
            e.preventDefault();
            setIsVideoExpanded(false);
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTrack, togglePlay, seekRelative, toggleMute, isVideoExpanded, setIsVideoExpanded]);
  
  // Mini player position style
  const getMiniPlayerStyle = () => {
    const baseStyle = {
      width: miniPlayerSize.width,
      height: miniPlayerSize.height,
      cursor: isDragging ? 'grabbing' : 'default',
    };
    
    if (miniPlayerPosition.x !== null && miniPlayerPosition.y !== null) {
      return {
        ...baseStyle,
        position: 'fixed',
        left: miniPlayerPosition.x,
        top: miniPlayerPosition.y,
        right: 'auto',
        bottom: 'auto',
        zIndex: 90,
      };
    }
    
    // Default position: bottom-right corner
    return {
      ...baseStyle,
      position: 'fixed',
      bottom: 100,
      right: 16,
      zIndex: 90,
    };
  };

  return (
    <>
      {/* ========== FREE TRIAL EXPIRED PAYWALL ========== */}
      <PaywallModal
        open={trialExpiredPaywall}
        onClose={() => {
          setTrialExpiredPaywall(false);
          closePlayer();
        }}
        episodeTitle={currentTrack?.title}
      />

      {/* ========== SESSION ERROR BANNER ========== */}
      {sessionError && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[200] 
                     bg-destructive/95 text-destructive-foreground px-6 py-3 rounded-lg 
                     shadow-lg flex items-center gap-3 max-w-md"
          data-testid="session-error-banner"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{sessionError}</p>
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 h-6 w-6 hover:bg-white/10"
            onClick={clearSessionError}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ========== MEDIA ERROR BANNER ========== */}
      {mediaError && !sessionError && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[200] 
                     bg-orange-600/95 text-white px-6 py-3 rounded-lg 
                     shadow-lg flex items-center gap-3 max-w-md"
          data-testid="media-error-banner"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{mediaError}</p>
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 h-6 w-6 hover:bg-white/10"
            onClick={clearMediaError}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ========== VIDEO EXPANDED OVERLAY ========== */}
      {showVideoExpandedOverlay && (
        <div 
          className="fixed inset-0 z-[95] bg-black/90 flex items-center justify-center"
          onClick={handleCollapse}
          data-testid="video-expanded-overlay"
        >
          {/* Header */}
          <div 
            className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent z-[105]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between max-w-[1200px] mx-auto">
              <div>
                <h2 className="text-white font-semibold text-lg">{currentTrack?.title}</h2>
                <p className="text-white/60 text-sm">{currentTrack?.creator_name}</p>
              </div>
              <div className="flex items-center gap-2">
                {queue.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowQueue(!showQueue);
                    }}
                  >
                    <ListMusic className="w-4 h-4 mr-2" />
                    Fronta ({queue.length})
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                  onClick={handleCollapse}
                  title="Zmenšit"
                >
                  <Minimize2 className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                  onClick={handleNativeFullscreen}
                  title="Celá obrazovka (nativní)"
                >
                  <Expand className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    closePlayer();
                  }}
                  title="Zavřít"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Video Canvas - Visual display of the video */}
          <VideoCanvas 
            expanded={true}
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}
          />
          
          {/* Center play indicator */}
          {!isPlaying && (
            <div 
              className="absolute z-[101] flex items-center justify-center pointer-events-none"
              style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="w-20 h-20 rounded-full bg-black/60 flex items-center justify-center">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
            </div>
          )}

          {/* Queue sidebar */}
          {showQueue && (
            <div
              className="absolute right-0 top-16 bottom-24 w-80 bg-black/95 border-l border-white/10 z-[110] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <QueuePanel dark onClose={() => setShowQueue(false)} />
            </div>
          )}

          {/* Bottom controls */}
          <div 
            className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-[105]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-[1200px] mx-auto">
              {/* Progress bar */}
              <div 
                className="h-1.5 bg-white/30 rounded-full cursor-pointer group mb-4"
                onClick={handleProgressClick}
              >
                <div 
                  className="h-full bg-primary rounded-full relative"
                  style={{ width: `${progressPercent}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Seek back */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={() => seekRelative(-10)}
                  >
                    <div className="relative">
                      <RotateCcw className="w-6 h-6" />
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">10</span>
                    </div>
                  </Button>

                  {/* Play/Pause */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-14 h-14 text-white bg-white/10 hover:bg-white/20 rounded-full"
                    onClick={togglePlay}
                    data-testid="expanded-play-button"
                  >
                    {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
                  </Button>

                  {/* Seek forward */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={() => seekRelative(10)}
                  >
                    <div className="relative">
                      <RotateCw className="w-6 h-6" />
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">10</span>
                    </div>
                  </Button>

                  {queue.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/10"
                      onClick={skipNext}
                    >
                      <SkipForward className="w-6 h-6" />
                    </Button>
                  )}

                  {/* Time */}
                  <span className="text-white/80 text-sm ml-2">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                {/* Volume */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={toggleMute}
                  >
                    {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  <Slider
                    value={[isMuted ? 0 : volume * 100]}
                    onValueChange={([val]) => changeVolume(val / 100)}
                    max={100}
                    step={1}
                    className="w-24"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== VIDEO MINI PLAYER (Draggable & Resizable) ========== */}
      {showVideoMiniPlayer && (
        <div
          ref={miniPlayerRef}
          className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black group/mini"
          style={getMiniPlayerStyle()}
          data-testid="floating-video-mini-player"
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
        >
          {/* Resize handle — corner grip */}
          <div
            className="absolute top-0 left-0 w-5 h-5 cursor-nw-resize z-50 flex items-center justify-center opacity-0 group-hover/mini:opacity-100 transition-opacity"
            onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e); }}
            title="Změnit velikost"
          >
            <GripVertical className="w-3 h-3 text-white/50" />
          </div>

          {/* Video Canvas — click to play/pause */}
          <div
            className="w-full h-full"
            style={{ cursor: isDragging ? 'grabbing' : 'default' }}
          >
            <VideoCanvas
              expanded={false}
              onClick={() => {
                if (!wasDragged) togglePlay();
              }}
            />
          </div>

          {/* Gradient: dark top + dark bottom */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80 pointer-events-none z-10" />

          {/* ── TOP BAR: title + creator + close ── */}
          <div className="absolute top-0 left-0 right-0 z-30 flex items-start gap-2 px-3 pt-2.5 pb-2">
            <div className="flex-1 min-w-0 pointer-events-none">
              <p className="text-white text-xs font-semibold truncate leading-tight drop-shadow">
                {currentTrack?.title}
              </p>
              <p className="text-white/55 text-[10px] truncate mt-0.5">
                {currentTrack?.creator_name}
              </p>
            </div>
            <button
              className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); closePlayer(); }}
              title="Zavřít"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>

          {/* ── CENTER: large play/pause indicator (shown when paused) ── */}
          {!isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
            >
              <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-5 h-5 text-white ml-0.5" />
              </div>
            </div>
          )}

          {/* ── BOTTOM CONTROLS (visible on hover) ── */}
          <div className="absolute bottom-5 left-0 right-0 z-30 px-2 py-1
                          opacity-0 group-hover/mini:opacity-100 transition-opacity duration-200
                          flex items-center gap-1">
            {/* Seek −10 */}
            <button
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/15 transition-colors text-white"
              onClick={(e) => { e.stopPropagation(); seekRelative(-10); }}
            >
              <div className="relative w-4 h-4">
                <RotateCcw className="w-4 h-4" />
                <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold mt-0.5">10</span>
              </div>
            </button>

            {/* Play / Pause */}
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 transition-colors text-white"
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              data-testid="mini-video-play-button"
            >
              {isPlaying
                ? <Pause className="w-4 h-4" />
                : <Play className="w-4 h-4 ml-0.5" />}
            </button>

            {/* Seek +10 */}
            <button
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/15 transition-colors text-white"
              onClick={(e) => { e.stopPropagation(); seekRelative(10); }}
            >
              <div className="relative w-4 h-4">
                <RotateCw className="w-4 h-4" />
                <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold mt-0.5">10</span>
              </div>
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Time */}
            {duration > 0 && (
              <span className="text-white/60 text-[10px] tabular-nums mr-1">
                {formatTime(currentTime)}/{formatTime(duration)}
              </span>
            )}

            {/* Back to video page */}
            <button
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/15 transition-colors text-white"
              onClick={(e) => { e.stopPropagation(); navigate(`/watch/${episodeWatchPath(currentTrack)}`); }}
              title="Zpět na video"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* ── PROGRESS BAR — always visible, clickable ── */}
          <div
            className="absolute bottom-0 left-0 right-0 z-40 cursor-pointer group/prog"
            style={{ height: 20 }}
            onClick={(e) => {
              e.stopPropagation();
              if (!duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - rect.left) / rect.width * duration);
            }}
          >
            <div className="absolute bottom-0 left-0 right-0 h-1 group-hover/prog:h-1.5 bg-white/25 transition-all duration-150">
              <div
                className="h-full bg-primary rounded-full transition-none"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ========== AUDIO MINI PLAYER (unified — same in all cases) ========== */}
      {showAudioPlayer && (
        <>
          {/* Queue panel */}
          {showQueue && (
            <div
              className="fixed bottom-[88px] right-4 z-50 w-80 h-[28rem] bg-card border border-border rounded-lg shadow-xl flex flex-col overflow-hidden"
              data-testid="queue-panel"
            >
              <QueuePanel onClose={() => setShowQueue(false)} />
            </div>
          )}

          {/* Unified mini bar */}
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-t border-border shadow-lg"
            data-testid="floating-player"
          >
            {/* Progress bar — full-width, clickable */}
            <div
              className="relative h-5 flex items-center cursor-pointer group"
              onClick={handleProgressClick}
            >
              <div className="absolute inset-x-0 h-1 group-hover:h-1.5 bg-muted transition-all rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-none"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-lg scale-0 group-hover:scale-100 transition-transform pointer-events-none"
                style={{ left: `calc(${progressPercent}% - 6px)` }}
              />
            </div>

            <div className="container-app py-3">
              <div className="flex items-center gap-4">
                {/* Track info — click navigates to episode */}
                <div
                  className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`/episode/${currentTrack?.id}`)}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate text-foreground">{currentTrack?.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{currentTrack?.creator_name}</p>
                  </div>
                </div>

                {/* Playback controls */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden sm:flex"
                    onClick={() => seekRelative(-10)}
                    data-testid="seek-back-10"
                  >
                    <div className="relative">
                      <RotateCcw className="w-5 h-5" />
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">10</span>
                    </div>
                  </Button>

                  <Button
                    variant="default"
                    size="icon"
                    className="w-11 h-11 rounded-full bg-primary hover:bg-primary/90"
                    onClick={togglePlay}
                    data-testid="floating-player-play"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden sm:flex"
                    onClick={() => seekRelative(10)}
                    data-testid="seek-forward-10"
                  >
                    <div className="relative">
                      <RotateCw className="w-5 h-5" />
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">10</span>
                    </div>
                  </Button>

                  {queue.length > 0 && (
                    <Button variant="ghost" size="icon" onClick={skipNext}>
                      <SkipForward className="w-5 h-5" />
                    </Button>
                  )}
                </div>

                {/* Time */}
                <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground min-w-[100px]">
                  <span>{formatTime(currentTime)}</span>
                  <span>/</span>
                  <span>{formatTime(duration)}</span>
                </div>

                {/* Volume */}
                <div className="hidden lg:flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={toggleMute}>
                    {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  <Slider
                    value={[isMuted ? 0 : volume * 100]}
                    onValueChange={([val]) => changeVolume(val / 100)}
                    max={100}
                    step={1}
                    className="w-24"
                  />
                </div>

                {/* Queue + Close */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowQueue(!showQueue)}
                    className={showQueue ? 'text-primary' : ''}
                    data-testid="queue-button"
                  >
                    <ListMusic className="w-4 h-4" />
                    {queue.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[10px] rounded-full flex items-center justify-center">
                        {queue.length}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closePlayer}
                    title="Zavřít"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * VideoCanvas - Displays the video stream from MediaEngine
 * 
 * This component "borrows" the video element from MediaEngine for visual display.
 * Uses canvas to draw video frames, keeping the actual video element in MediaEngine.
 */
function VideoCanvas({ expanded, onClick }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const currentTrack = usePlayerStore(state => state.currentTrack);
  const isPlaying = usePlayerStore(state => state.isPlaying);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const videoElement = document.querySelector('[data-testid="media-engine-video"]');
    
    if (!videoElement || currentTrack?.media_type !== 'video') {
      // Clear canvas if no video
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    }
    
    const drawFrame = () => {
      try {
        if (videoElement.readyState >= 2) {
          // Set canvas size to match container
          const rect = canvas.getBoundingClientRect();
          if (canvas.width !== rect.width || canvas.height !== rect.height) {
            canvas.width = rect.width;
            canvas.height = rect.height;
          }
          
          // Calculate aspect ratio to maintain 16:9
          const videoRatio = videoElement.videoWidth / videoElement.videoHeight;
          const canvasRatio = canvas.width / canvas.height;
          
          let drawWidth, drawHeight, offsetX, offsetY;
          
          if (videoRatio > canvasRatio) {
            drawWidth = canvas.width;
            drawHeight = canvas.width / videoRatio;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            drawHeight = canvas.height;
            drawWidth = canvas.height * videoRatio;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }
          
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(videoElement, offsetX, offsetY, drawWidth, drawHeight);
        }
      } catch (e) {
        // Handle cross-origin canvas tainting - show black canvas
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      animationRef.current = requestAnimationFrame(drawFrame);
    };
    
    drawFrame();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentTrack?.id, currentTrack?.media_type, isPlaying]);
  
  const style = expanded ? {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: '1200px',
    aspectRatio: '16/9',
    maxHeight: '80vh',
    zIndex: 100,
    borderRadius: '12px',
  } : {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  };
  
  return (
    <canvas
      ref={canvasRef}
      className="bg-black cursor-pointer"
      style={style}
      onClick={onClick}
      data-testid="video-canvas"
    />
  );
}
